import { useMemo, useState, useEffect, useRef } from "react";
import {
  MapPin,
  Users,
  Wifi,
  Book,
  Utensils,
  Dumbbell,
  Shield,
  Phone,
  Mail,
  Building2,
  Clock,
  Calendar,
  ArrowLeft,
  Camera,
  Bed,
  GraduationCap,
  Activity,
  Home,
  Globe,
  UserCircle,
  FileCheck,
  Play,
  Briefcase,
  Download,
} from "lucide-react";
import {
  statesAPI,
  citiesAPI,
  designationMasterAPI,
} from "../../../Jainboarding_Admin/services/api";
import defaultInstitutionImage from "../../assets/hotels1.png";

interface EducationalYearItem {
  total_capacity: number;
  vacant: number;
  occupied: number;
  educational_year: string;
}

interface Hostel {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  city_name?: string;
  state_name?: string;
  capacity: number;
  type: string;
  facilities: string[];
  fees: string;
  scholarship: boolean;
  sicRating: number;
  image: string;
  trust: string;
  safetyRating: number;
  hygieneRating: number;
  academicSupport: number;
  culturalAlignment: number;
  governance: number;
  levels?: string[];
  teachingStaff?: number;
  nonTeachingStaff?: number;
  classrooms?: number;
  trusteeCount?: number;
  contactPerson?: string;
  email?: string;
  phone?: string;
  website?: string;
  images?: string[];
  selected_facilities?: string[];
  selectedFacilities?: string[];
  educational_year?: EducationalYearItem[];
  selected_levels?: string[];
  medium?: string[] | string;
  boarding_gender?: string;
  year_established?: string | number;
  recognition_number?: string;
  total_students_in_school?: string | number | null;
  total_classroom_in_school?: string | number | null;
  total_students_in_hostel?: string | number | null;
  total_rooms_in_hostel?: string | number | null;
  visiting_hours?: {
    office_hours?: string;
    visit_days?: string;
  };
  campus_area?: string;
  trustee_contact?: string;
  trustee_email?: string;
  trust_registration?: string;
  other_trustees?: string;
  trustee_designation?: string;
  contact_person?: string;
  designation?: string;
  admission_link?: string;
  brochure?: string;
}

interface HostelDetailPageProps {
  hostel: Hostel;
  onBack: () => void;
}

const getAmenityIcon = (amenity: string) => {
  const key = amenity.toLowerCase();
  if (key.includes("library")) {
    return <Book className="h-5 w-5" />;
  }
  if (key.includes("wifi") || key.includes("wi-fi")) {
    return <Wifi className="h-5 w-5" />;
  }
  if (key.includes("mess") || key.includes("dining")) {
    return <Utensils className="h-5 w-5" />;
  }
  if (key.includes("gym")) {
    return <Dumbbell className="h-5 w-5" />;
  }
  if (key.includes("security")) {
    return <Shield className="h-5 w-5" />;
  }
  if (key.includes("study")) {
    return <GraduationCap className="h-5 w-5" />;
  }
  if (key.includes("sports") || key.includes("playground")) {
    return <Activity className="h-5 w-5" />;
  }
  if (key.includes("room")) {
    return <Bed className="h-5 w-5" />;
  }
  return <Home className="h-5 w-5" />;
};

export default function HostelDetailPage({
  hostel,
  onBack,
}: HostelDetailPageProps) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [resolvedCity, setResolvedCity] = useState<string | null>(null);
  const [resolvedState, setResolvedState] = useState<string | null>(null);
  const [resolvedDesignation, setResolvedDesignation] = useState<string | null>(
    null
  );
  const [resolvedTrusteeDesignation, setResolvedTrusteeDesignation] = useState<
    string | null
  >(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    const resolveLabels = async () => {
      if (fetchingRef.current) {
        return;
      }
      fetchingRef.current = true;

      try {
        const sId = hostel.state;
        const cId = hostel.city;

        // Only fetch if it looks like an ID (numeric) and name is missing
        if (sId && !hostel.state_name && !isNaN(Number(sId))) {
          const s = await statesAPI.get(sId);
          if (s?.name) {
            setResolvedState(s.name);
          }
        }

        if (cId && !hostel.city_name && !isNaN(Number(cId))) {
          const c = await citiesAPI.get(cId);
          if (c?.name) {
            setResolvedCity(c.name);
          }
        }

        const dId = hostel.designation;
        if (dId && !isNaN(Number(dId))) {
          const d = await designationMasterAPI.get(dId);
          if (d?.name) {
            setResolvedDesignation(d.name);
          }
        }

        const tdId = hostel.trustee_designation;
        if (tdId && !isNaN(Number(tdId))) {
          const d = await designationMasterAPI.get(tdId);
          if (d?.name) {
            setResolvedTrusteeDesignation(d.name);
          }
        }
      } catch (err) {
        console.error("Error resolving labels:", err);
      } finally {
        fetchingRef.current = false;
      }
    };

    resolveLabels();
  }, [
    hostel.city,
    hostel.state,
    hostel.city_name,
    hostel.state_name,
    hostel.designation,
    hostel.trustee_designation,
  ]);

  const amenities = useMemo(() => {
    if (
      Array.isArray(hostel.selected_facilities) &&
      hostel.selected_facilities.length
    ) {
      return hostel.selected_facilities;
    }
    if (
      Array.isArray(hostel.selectedFacilities) &&
      hostel.selectedFacilities.length
    ) {
      return hostel.selectedFacilities;
    }
    return hostel.facilities ?? [];
  }, [
    hostel.facilities,
    hostel.selectedFacilities,
    hostel.selected_facilities,
  ]);

  const images = useMemo(() => {
    if (Array.isArray(hostel.images) && hostel.images.length > 0) {
      return hostel.images.filter(Boolean);
    }
    if (hostel.image && String(hostel.image).trim()) {
      return [hostel.image];
    }
    return [];
  }, [hostel.image, hostel.images]);

  const isVideoUrl = (url: string) => {
    if (!url) {
      return false;
    }
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
    return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
  };

  const hasImages = images.length > 0;
  const safeImages = hasImages ? images : [defaultInstitutionImage];

  const hasRealTrustData = () => {
    const trustName = hostel.trust?.trim();
    if (trustName && trustName !== "JBF Registered Trust") {
      return true;
    }
    return !!(
      hostel.trustee_designation ??
      hostel.trustee_contact ??
      hostel.trustee_email ??
      hostel.trust_registration ??
      hostel.other_trustees
    );
  };

  const hasVisitingHours =
    (hostel.visiting_hours?.office_hours?.trim()?.length ?? 0) > 0 ||
    (hostel.visiting_hours?.visit_days?.trim()?.length ?? 0) > 0;

  // const educationalYears = Array.isArray(hostel.educational_year)
  //   ? hostel.educational_year
  //   : [];
  const selectedLevels = Array.isArray(hostel.selected_levels)
    ? hostel.selected_levels
    : (hostel.levels ?? []);
  const boardingGender = hostel.boarding_gender ?? null;
  const schoolStudentCapacity = Number(
    hostel.total_students_in_school ?? hostel.capacity ?? 0
  );
  const schoolClassroomCapacity = Number(
    hostel.total_classroom_in_school ?? hostel.classrooms ?? 0
  );
  const hostelStudentCapacity = Number(hostel.total_students_in_hostel ?? 0);
  const hostelRoomCapacity = Number(hostel.total_rooms_in_hostel ?? 0);
  const mediumValues = Array.isArray(hostel.medium)
    ? hostel.medium
    : typeof hostel.medium === "string" && hostel.medium
      ? hostel.medium.split("/").filter(Boolean)
      : [];

  const formatLevelLabel = (level: string) =>
    level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
  const formatMediumLabel = (medium: string) =>
    medium.charAt(0).toUpperCase() + medium.slice(1).toLowerCase();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-[5rem] z-50 border-b border-gray-200 bg-white lg:top-[8.5rem]">
        <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 transition-colors hover:text-[#7A1E1E]"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Directory</span>
            </button>
            {/* <div className="flex items-center gap-3">
              <button className="rounded-lg border border-gray-200 p-2 transition-all hover:border-[#D4A017] hover:bg-[#FFF9F0]">
                <Share2 className="h-5 w-5 text-gray-600" />
              </button>
              <button className="rounded-lg border border-gray-200 p-2 transition-all hover:border-[#D4A017] hover:bg-[#FFF9F0]">
                <Heart className="h-5 w-5 text-gray-600" />
              </button>
            </div> */}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="space-y-8 lg:col-span-2">
            {/* Image Gallery */}
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              {hasImages ? (
                <>
                  <div className="relative h-[400px] bg-gray-900">
                    {isVideoUrl(safeImages[selectedImage]) ? (
                      <video
                        src={safeImages[selectedImage]}
                        className="h-full w-full object-contain"
                        controls
                        autoPlay
                        muted
                      />
                    ) : (
                      <img
                        src={safeImages[selectedImage]}
                        alt={hostel.name}
                        className="h-full w-full object-cover"
                      />
                    )}
                    {/* <div className="absolute right-4 top-4 flex items-center gap-1 rounded-lg bg-[#D4A017] px-3 py-1 text-white">
                      <Star className="h-4 w-4 fill-current" />
                      <span>{hostel.sicRating ?? 0}</span>
                    </div> */}
                    <div className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg bg-black/50 px-3 py-1 text-white backdrop-blur-sm">
                      <Camera className="h-4 w-4" />
                      <span>
                        {selectedImage + 1} / {safeImages.length}
                      </span>
                    </div>
                  </div>
                  <div className="scrollbar-hide flex gap-3 overflow-x-auto p-4">
                    {safeImages.map((image, index) => {
                      const isVideo = isVideoUrl(image);
                      return (
                        <button
                          key={
                            typeof image === "string" ? image : `image-${index}`
                          }
                          onClick={() => setSelectedImage(index)}
                          className={`relative h-20 w-32 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                            selectedImage === index
                              ? "border-[#D4A017]"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {isVideo ? (
                            <div className="relative h-full w-full bg-black">
                              <video
                                src={image}
                                className="h-full w-full object-cover opacity-50"
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Play className="h-6 w-6 text-white" />
                              </div>
                            </div>
                          ) : (
                            <img
                              src={image}
                              alt={`View ${index + 1}`}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 bg-gray-100 py-24 text-gray-500">
                  <Camera className="h-16 w-16 text-gray-400" />
                  <p className="text-lg font-medium">No images available</p>
                  <p className="text-sm">
                    Image data is not available for this institution.
                  </p>
                </div>
              )}
            </div>

            {/* Overview */}
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <h1 className="mb-4 text-3xl text-[#7A1E1E]">{hostel.name}</h1>

              <div className="mb-6 flex flex-wrap items-center gap-6 text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-[#7A1E1E]" />
                  <span>{hostel.address || "No data available"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-[#7A1E1E]" />
                  <span>{hostel.type || "—"}</span>
                </div>
                {/* <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 fill-current text-[#D4A017]" />
                  <span>{hostel.sicRating ?? 0} rating</span>
                </div> */}
              </div>

              <div className="mb-6 flex flex-wrap items-center gap-3">
                {boardingGender && (
                  <div
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 ${
                      boardingGender.toLowerCase() === "boys"
                        ? "bg-blue-50 text-blue-800"
                        : boardingGender.toLowerCase() === "girls"
                          ? "bg-rose-50 text-rose-800"
                          : "bg-violet-50 text-violet-800"
                    }`}
                  >
                    <Users className="h-5 w-5" />
                    <span className="capitalize">
                      {boardingGender.toLowerCase() === "boys"
                        ? "Boys Hostel"
                        : boardingGender.toLowerCase() === "girls"
                          ? "Girls Hostel"
                          : boardingGender.toLowerCase().includes("/")
                            ? "Boys & Girls Hostel"
                            : `${boardingGender} Hostel`}
                    </span>
                  </div>
                )}
              </div>

              <p className="mb-6 leading-relaxed text-gray-700">
                {(hostel.contact_person ?? hostel.contactPerson)
                  ? `${hostel.name} is managed by ${hostel.contact_person ?? hostel.contactPerson} and serves students with a focus on safe, value-driven accommodation.`
                  : `${hostel.name} is a JBF listed institution providing student accommodation and support services.`}
              </p>

              <div className="rounded-xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-6">
                <div className="grid gap-6 lg:grid-cols-[1fr_auto_1fr] lg:items-start">
                  <div>
                    <div className="mb-4 text-center text-xl font-semibold text-[#7A1E1E]">
                      School
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="rounded-xl border border-[#D4A017]/15 bg-white/90 p-4 text-center">
                        <div className="mb-1 text-2xl text-[#7A1E1E]">
                          {schoolStudentCapacity.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">
                          Total Students
                        </div>
                      </div>
                      <div className="rounded-xl border border-[#D4A017]/15 bg-white/90 p-4 text-center">
                        <div className="mb-1 text-2xl text-[#7A1E1E]">
                          {schoolClassroomCapacity.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">
                          Total Classroom
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="hidden w-px self-stretch bg-[#D4A017]/30 lg:block" />

                  <div>
                    <div className="mb-4 text-center text-xl font-semibold text-[#7A1E1E]">
                      Hostel
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="rounded-xl border border-[#D4A017]/15 bg-white/90 p-4 text-center">
                        <div className="mb-1 text-2xl text-[#7A1E1E]">
                          {hostelStudentCapacity.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">
                          Total Students
                        </div>
                      </div>
                      <div className="rounded-xl border border-[#D4A017]/15 bg-white/90 p-4 text-center">
                        <div className="mb-1 text-2xl text-[#7A1E1E]">
                          {hostelRoomCapacity.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">Total Rooms</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Academic & Institution Details */}
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <h2 className="mb-6 text-2xl text-[#7A1E1E]">
                Academic & Institution Details
              </h2>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                  <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                    Year Established
                  </div>
                  <div className="text-lg text-gray-800">
                    {hostel.year_established ?? "No data available"}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                  <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                    Registration Number
                  </div>
                  <div className="text-lg text-gray-800">
                    {hostel.recognition_number ?? "No data available"}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                  <div className="mb-3 text-xs uppercase tracking-wide text-gray-500">
                    Medium of Instruction
                  </div>
                  {mediumValues.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {mediumValues.map(medium => (
                        <span
                          key={medium}
                          className="inline-flex items-center rounded-lg border border-[#D4A017]/30 bg-[#FFF9F0] px-3 py-1.5 text-sm font-medium text-[#7A1E1E]"
                        >
                          {formatMediumLabel(medium)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-lg text-gray-800">
                      No data available
                    </div>
                  )}
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                  <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                    Campus Area
                  </div>
                  <div className="text-lg text-gray-800">
                    {hostel.campus_area ?? "No data available"}
                  </div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <h2 className="mb-6 text-2xl text-[#7A1E1E]">
                Amenities & Facilities
              </h2>

              {amenities && amenities.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {amenities.map(amenity => (
                    <div
                      key={amenity}
                      className="flex items-center gap-3 rounded-xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-4"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                        {getAmenityIcon(amenity)}
                      </div>
                      <span className="text-sm text-gray-700">{amenity}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50/50 py-10 text-gray-500">
                  <Home className="h-12 w-12 text-gray-400" />
                  <p className="font-medium">No data available</p>
                  <p className="text-sm">
                    Amenities and facilities have not been provided.
                  </p>
                </div>
              )}
            </div>

            {/* Management */}
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <h2 className="mb-6 text-2xl text-[#7A1E1E]">Management</h2>

              {hasRealTrustData() ? (
                <div className="space-y-4">
                  {hostel.trust?.trim() &&
                    hostel.trust.trim() !== "JBF Registered Trust" && (
                      <div className="flex items-start gap-4 rounded-xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-6">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                          <Building2 className="h-6 w-6" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="mb-1 text-lg font-semibold text-[#7A1E1E]">
                            {hostel.trust}
                          </h3>
                          {hostel.trustee_designation && (
                            <p className="text-sm capitalize text-gray-600">
                              {resolvedTrusteeDesignation ??
                                hostel.trustee_designation.replace(/_/g, " ")}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  {hostel.trustee_designation &&
                    (!hostel.trust?.trim() ||
                      hostel.trust.trim() === "JBF Registered Trust") && (
                      <div className="flex items-start gap-4 rounded-xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-6">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                          <Building2 className="h-6 w-6" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm capitalize text-gray-600">
                            {resolvedTrusteeDesignation ??
                              hostel.trustee_designation.replace(/_/g, " ")}
                          </p>
                        </div>
                      </div>
                    )}

                  {(hostel.trustee_contact ??
                    hostel.trustee_email ??
                    hostel.trust_registration ??
                    hostel.other_trustees) && (
                    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                      <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#7A1E1E]">
                        <UserCircle className="h-4 w-4" />
                        Trustee Details
                      </h4>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {hostel.trustee_contact && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 flex-shrink-0 text-[#7A1E1E]" />
                            <div>
                              <div className="text-xs text-gray-500">
                                Trustee Contact
                              </div>
                              <a
                                href={`tel:${hostel.trustee_contact}`}
                                className="text-sm text-[#7A1E1E] hover:underline"
                              >
                                {hostel.trustee_contact}
                              </a>
                            </div>
                          </div>
                        )}
                        {hostel.trustee_email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 flex-shrink-0 text-[#7A1E1E]" />
                            <div>
                              <div className="text-xs text-gray-500">
                                Trustee Email
                              </div>
                              <a
                                href={`mailto:${hostel.trustee_email}`}
                                className="break-all text-sm text-[#7A1E1E] hover:underline"
                              >
                                {hostel.trustee_email}
                              </a>
                            </div>
                          </div>
                        )}
                        {hostel.trust_registration && (
                          <div className="flex items-center gap-2">
                            <FileCheck className="h-4 w-4 flex-shrink-0 text-[#7A1E1E]" />
                            <div>
                              <div className="text-xs text-gray-500">
                                Trust Registration
                              </div>
                              <p className="text-sm text-gray-700">
                                {hostel.trust_registration}
                              </p>
                            </div>
                          </div>
                        )}
                        {hostel.other_trustees && (
                          <div className="flex items-start gap-2 sm:col-span-2">
                            <UserCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#7A1E1E]" />
                            <div>
                              <div className="text-xs text-gray-500">
                                Other Trustees
                              </div>
                              <p className="text-sm text-gray-700">
                                {hostel.other_trustees}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50/50 py-12 text-gray-500">
                  <Building2 className="h-12 w-12 text-gray-400" />
                  <p className="font-medium">
                    No management / trustee data available
                  </p>
                  <p className="text-sm">
                    Data for this section has not been provided.
                  </p>
                </div>
              )}
            </div>

            {/* Educational Year */}
            {/*{educationalYears.length > 0 && (
              <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                <h2 className="mb-6 text-2xl font-semibold text-[#7A1E1E]">
                  Educational Year
                </h2>
                <div className="flex flex-wrap gap-3">
                  {educationalYears.map((item, _index) => (
                    <div
                      key={`${item.educational_year}`}
                      className="inline-flex items-center gap-3 rounded-full border border-[#E0C7A1] bg-[#FDF3E5] px-5 py-3"
                    >
                      <Calendar className="h-5 w-5 flex-shrink-0 text-[#7A1E1E]" />
                      <div>
                        <div className="font-semibold text-[#7A1E1E]">
                          {item.educational_year}
                        </div>
                        <div className="mt-0.5 flex flex-col gap-1 text-sm text-[#6F2A28]/80">
                          <span>Capacity: {item.total_capacity}</span>
                          <div className="flex gap-3">
                            <span>Occupied: {item.occupied}</span>
                            <span>Vacant: {item.vacant}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price & Apply */}
            {[hostel.admission_link, hostel.brochure, hostel.website].some(
              Boolean
            ) && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="space-y-3">
                  {hostel.admission_link && (
                    <a
                      href={
                        hostel.admission_link.startsWith("http")
                          ? hostel.admission_link
                          : `https://${hostel.admission_link}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#7A1E1E] to-[#8B2E2E] py-3 font-medium text-white transition-all hover:shadow-lg"
                    >
                      Admission Now
                    </a>
                  )}
                  {hostel.brochure && (
                    <a
                      href={hostel.brochure}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-[#D4A017] bg-[#FFF9F0] py-3 font-medium text-[#7A1E1E] transition-all hover:bg-[#FFF2E0]"
                    >
                      <Download className="h-5 w-5" />
                      Download Brochure
                    </a>
                  )}
                  {hostel.website && (
                    <a
                      href={
                        hostel.website.startsWith("http")
                          ? hostel.website
                          : `https://${hostel.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <button className="w-full rounded-xl border border-gray-200 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50">
                        Schedule Visit
                      </button>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg text-[#7A1E1E]">
                Contact Information
              </h3>

              <div className="space-y-4">
                {(hostel.contact_person ?? hostel.contactPerson) && (
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFF9F0]">
                      <UserCircle className="h-5 w-5 text-[#7A1E1E]" />
                    </div>
                    <div>
                      <div className="mb-1 text-sm text-gray-500">
                        Contact Person
                      </div>
                      <p className="font-medium text-gray-800">
                        {hostel.contact_person ?? hostel.contactPerson}
                      </p>
                      {(hostel.designation ?? resolvedDesignation) && (
                        <p className="text-xs capitalize text-gray-500">
                          {resolvedDesignation ??
                            hostel.designation?.replace(/_/g, " ")}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFF9F0]">
                    <Phone className="h-5 w-5 text-[#7A1E1E]" />
                  </div>
                  <div>
                    <div className="mb-1 text-sm text-gray-500">Phone</div>
                    <a
                      href={`tel:${hostel.phone ?? ""}`}
                      className="text-[#7A1E1E] hover:underline"
                    >
                      {hostel.phone ?? "N/A"}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFF9F0]">
                    <Mail className="h-5 w-5 text-[#7A1E1E]" />
                  </div>
                  <div>
                    <div className="mb-1 text-sm text-gray-500">Email</div>
                    <a
                      href={`mailto:${hostel.email ?? ""}`}
                      className="break-all text-[#7A1E1E] hover:underline"
                    >
                      {hostel.email ?? "N/A"}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFF9F0]">
                    <MapPin className="h-5 w-5 text-[#7A1E1E]" />
                  </div>
                  <div>
                    <div className="mb-1 text-sm text-gray-500">Address</div>
                    <p className="text-gray-700">
                      {hostel.address},{" "}
                      {resolvedCity ?? hostel.city_name ?? hostel.city},{" "}
                      {resolvedState ?? hostel.state_name ?? hostel.state}
                    </p>
                  </div>
                </div>

                {hostel.website && (
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFF9F0]">
                      <Globe className="h-5 w-5 text-[#7A1E1E]" />
                    </div>
                    <div>
                      <div className="mb-1 text-sm text-gray-500">Website</div>
                      <a
                        href={
                          hostel.website.startsWith("http")
                            ? hostel.website
                            : `https://${hostel.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-[#7A1E1E] hover:underline"
                      >
                        {hostel.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Visiting Hours */}
            <div className="rounded-2xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg text-[#7A1E1E]">Visiting Hours</h3>

              {hasVisitingHours ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 flex-shrink-0 text-[#7A1E1E]" />
                    <div>
                      <div className="text-sm text-gray-500">Office Hours</div>
                      <div className="text-gray-700">
                        {hostel.visiting_hours?.office_hours ?? "—"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 flex-shrink-0 text-[#7A1E1E]" />
                    <div>
                      <div className="text-sm text-gray-500">Visit Days</div>
                      <div className="text-gray-700">
                        {hostel.visiting_hours?.visit_days ?? "—"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-4 text-center text-sm text-gray-500">
                  No data available
                </div>
              )}
            </div>

            {/* Education Level (sidebar) */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg text-[#7A1E1E]">Education Level</h3>
              {selectedLevels.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedLevels.map(level => (
                    <span
                      key={level}
                      className="inline-flex items-center gap-1 rounded-lg border border-[#D4A017]/30 bg-[#FFF9F0] px-3 py-1.5 text-sm font-medium capitalize text-[#7A1E1E]"
                    >
                      <GraduationCap className="h-4 w-4" />
                      {formatLevelLabel(level)}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center text-sm text-gray-500">
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
